import React, { useRef, useEffect, useMemo, useState, Suspense } from "react";
import { Canvas, useFrame, useThree, extend } from "@react-three/fiber";
import { useGLTF, useAnimations, Text, shaderMaterial, OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import gsap from "gsap";

// ─── Tunables ────────────────────────────────────────────────────────────────
// Any .ttf/.otf in /public/fonts works. Others to try: Righteous-Regular.ttf,
// spartan.ttf, more_projects_uni_sans.otf, kugile_regular.ttf …
const FONT = "/fonts/milker.otf";
const FONT_FAMILY = "cbSceneFont"; // runtime-registered name used for measuring
const LINES = ["HEY!", "I'M RAM"];
const FONT_SIZE = 1.0; // world units (em); the whole block is auto-fit to the view
const BUTTERFLY_SCALE = 0.4;
// Resting pose — tune so the butterfly sits facing the viewer, wings open.
const REST_EULER = new THREE.Euler(0, Math.PI / 2, 0);
const LETTER_PRESS = 0.18; // how far the perched letter sinks under the weight
const PERCH_HEIGHT = 0.259; // butterfly height above the glyph centre (em fraction)
const PERCH_Z = 0.2; // depth relative to the letter plane; 0 = exactly on top, + = toward camera
// Letters whose top-centre is empty (a gap or valley). For these the butterfly
// perches on a tall side stroke instead of floating over the middle.
const CENTER_GAP_LETTERS = new Set(["H", "M", "N", "U", "V", "W", "X", "Y", "K", "L"]);
const SIDE_OFFSET = 0.33; // fraction of glyph width to shift onto the side stroke
const LINE_GAP = 0.55; // half the vertical gap between the two lines (em); lower = closer
const TILT = 0; // max scene tilt (radians) following the mouse; lower = subtler
const FLIGHT_TIME = 2.5; // seconds in the air between letters
const SIT_TIME = 3; // seconds resting on a letter  → ~4s per hop

// ─── Letter wave material ────────────────────────────────────────────────────
// Every letter carries this. Inactive → plain white. When the butterfly lands,
// uActive eases to 1 and an upward-flowing, colourful wave animates over the
// glyph (troika keeps the letter's shape; we only control the colour).
const LetterWaveMaterial = shaderMaterial(
  {
    uTime: 0,
    uActive: 0,
    uColorA: new THREE.Color("#ff4d8d"),
    uColorB: new THREE.Color("#7b5cff"),
    uColorC: new THREE.Color("#31d0ff"),
  },
  /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  /* glsl */ `
    uniform float uTime;
    uniform float uActive;
    uniform vec3 uColorA;
    uniform vec3 uColorB;
    uniform vec3 uColorC;
    varying vec2 vUv;

    void main() {
      float h = vUv.y; // 0 at glyph bottom → 1 at top

      // Vertical gradient through three colours…
      vec3 grad = mix(uColorA, uColorB, smoothstep(0.0, 0.5, h));
      grad = mix(grad, uColorC, smoothstep(0.5, 1.0, h));

      // …with bands of light flowing upward over time.
      float wave = sin(h * 11.0 - uTime * 3.2) * 0.5 + 0.5;
      float wave2 = sin(h * 6.0 - uTime * 1.8 + vUv.x * 4.0) * 0.5 + 0.5;
      vec3 col = grad + 0.28 * wave * (0.4 + 0.6 * wave2);
      col += 0.10 * sin(uTime * 2.0 + h * 22.0); // subtle shimmer

      vec3 base = vec3(1.0); // plain white when idle
      vec3 finalCol = mix(base, col, clamp(uActive, 0.0, 1.0));
      gl_FragColor = vec4(finalCol, 1.0);
    }
  `
);
extend({ LetterWaveMaterial });

// Lay out each glyph along its line using the loaded web font's real metrics,
// centred around the origin. Returns { glyphs:[{char,x,y}], maxWidth, height }.
function computeLayout(family) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const refPx = 100;
  ctx.font = `${refPx}px "${family}"`;
  const scale = FONT_SIZE / refPx;

  const glyphs = [];
  let maxWidth = 0;
  LINES.forEach((line, li) => {
    const widths = [...line].map((ch) => ctx.measureText(ch).width * scale);
    const total = widths.reduce((a, b) => a + b, 0);
    maxWidth = Math.max(maxWidth, total);
    const y = li === 0 ? FONT_SIZE * LINE_GAP : -FONT_SIZE * LINE_GAP;
    let x = -total / 2;
    [...line].forEach((ch, ci) => {
      const w = widths[ci];
      if (ch !== " ") glyphs.push({ char: ch, x: x + w / 2, y, w });
      x += w;
    });
  });
  return { glyphs, maxWidth, height: FONT_SIZE * 2.4 };
}

// Quadratic bezier so the butterfly arcs between letters.
function bezier(out, t, p0, p1, p2) {
  const u = 1 - t;
  out.set(
    u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
    u * u * p0.z + 2 * u * t * p1.z + t * t * p2.z
  );
  return out;
}

function Letters({ glyphs, press }) {
  const refs = useRef([]);
  const mats = useRef([]);
  const coloredIndex = useRef(-1);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();

    // Give the freshly-perched letter a new random colour scheme (once).
    if (press.current.index >= 0 && press.current.index !== coloredIndex.current) {
      coloredIndex.current = press.current.index;
      const mat = mats.current[press.current.index];
      if (mat) {
        const hue = Math.random();
        mat.uColorA.setHSL(hue, 0.85, 0.6);
        mat.uColorB.setHSL((hue + 0.12) % 1, 0.9, 0.55);
        mat.uColorC.setHSL((hue + 0.28) % 1, 0.95, 0.62);
        // Share this letter's colour so the butterfly's wings can match it.
        press.current.color.setHSL(hue, 0.85, 0.6);
      }
    }

    glyphs.forEach((L, i) => {
      const mesh = refs.current[i];
      const mat = mats.current[i];
      const pressed = press.current.index === i;
      if (mesh) {
        const targetY = L.y - (pressed ? LETTER_PRESS : 0);
        mesh.position.y += (targetY - mesh.position.y) * 0.15;
      }
      if (mat) {
        mat.uTime = time;
        mat.uActive += ((pressed ? 1 : 0) - mat.uActive) * 0.12;
      }
    });
  });

  return glyphs.map((L, i) => (
    <Text
      key={i}
      ref={(el) => (refs.current[i] = el)}
      font={FONT}
      fontSize={FONT_SIZE}
      anchorX="center"
      anchorY="middle"
      position={[L.x, L.y, 0]}
    >
      {L.char}
      <letterWaveMaterial
        ref={(el) => (mats.current[i] = el)}
        transparent
        toneMapped={false}
      />
    </Text>
  ));
}

function Butterfly({ glyphs, press }) {
  const group = useRef();
  const { scene, animations } = useGLTF("/animated_butterfly.glb");
  const { actions } = useAnimations(animations, group);

  const from = useRef(new THREE.Vector3());
  const to = useRef(new THREE.Vector3());
  const ctrl = useRef(new THREE.Vector3());
  const prog = useRef({ t: 1 });
  const flying = useRef(false);
  const current = useRef(-1);
  const pending = useRef(null);
  const mounted = useRef(true);
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tmpAhead = useMemo(() => new THREE.Vector3(), []);
  // Resting orientation flips its yaw (±90°) on every hop, so the butterfly
  // alternates which way it faces from letter to letter.
  const restQuat = useMemo(() => new THREE.Quaternion(), []);
  const restDir = useRef(-1);
  const flipRestYaw = () => {
    restDir.current *= -1;
    restQuat.setFromEuler(
      new THREE.Euler(REST_EULER.x, restDir.current * Math.abs(REST_EULER.y), REST_EULER.z)
    );
  };

  const perchOf = (i) => {
    const g = glyphs[i];
    // Gap-topped letters: shift onto a tall side stroke instead of the middle.
    const xOff = CENTER_GAP_LETTERS.has(g.char) ? -g.w * SIDE_OFFSET : 0;
    return new THREE.Vector3(g.x + xOff, g.y + FONT_SIZE * PERCH_HEIGHT, PERCH_Z);
  };

  // Grab the wing material so we can tint it to the perched letter's colour,
  // remembering its defaults to fade back to while flying.
  const wingMats = useRef([]);
  useEffect(() => {
    const mats = [];
    scene.traverse((o) => {
      if (o.isMesh && o.material.name === "Wings" && !mats.includes(o.material)) {
        o.material.userData.origColor = o.material.color.clone();
        o.material.userData.origEmissive = o.material.emissive.clone();
        mats.push(o.material);
      }
    });
    wingMats.current = mats;
    return () =>
      mats.forEach((m) => {
        m.color.copy(m.userData.origColor);
        m.emissive.copy(m.userData.origEmissive);
      });
  }, [scene]);

  const pickOther = (cur) => {
    if (glyphs.length < 2) return 0;
    let n;
    do {
      n = Math.floor(Math.random() * glyphs.length);
    } while (n === cur);
    return n;
  };

  const playFly = () => {
    actions["Idle"]?.fadeOut(0.3);
    actions["Flying"]?.reset().fadeIn(0.3).play();
  };
  const playIdle = () => {
    actions["Flying"]?.fadeOut(0.4);
    actions["Idle"]?.reset().fadeIn(0.4).play();
  };

  const flyTo = (next) => {
    if (!mounted.current || !group.current) return;
    from.current.copy(group.current.position);
    to.current.copy(perchOf(next));
    // Lift the control point up + toward the camera for a nice arc.
    ctrl.current.copy(from.current).add(to.current).multiplyScalar(0.5);
    ctrl.current.y += 1.4;
    ctrl.current.z += 1.5;

    press.current.index = -1; // release the letter we're leaving
    flying.current = true;
    flipRestYaw(); // alternate facing for this new perch
    playFly();
    prog.current.t = 0;
    gsap.to(prog.current, {
      t: 1,
      duration: FLIGHT_TIME,
      ease: "power2.inOut",
      onComplete: () => {
        if (!mounted.current) return;
        flying.current = false;
        current.current = next;
        press.current.index = next; // this letter sinks under the weight
        playIdle();
        pending.current = gsap.delayedCall(SIT_TIME, () =>
          flyTo(pickOther(next))
        );
      },
    });
  };

  // Start off-screen and swoop onto the first letter.
  useEffect(() => {
    mounted.current = true;
    const first = Math.floor(Math.random() * glyphs.length);
    if (group.current) {
      group.current.position.copy(perchOf(first)).add(new THREE.Vector3(6, 4, 3));
    }
    flyTo(first);
    return () => {
      mounted.current = false;
      gsap.killTweensOf(prog.current);
      pending.current?.kill();
      Object.values(actions).forEach((a) => a?.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((state) => {
    if (!group.current) return;

    const t = prog.current.t;
    const time = state.clock.getElapsedTime();

    bezier(tmp, t, from.current, ctrl.current, to.current);
    group.current.position.copy(tmp);

    if (flying.current && t < 0.985) {
      bezier(tmpAhead, Math.min(t + 0.02, 1), from.current, ctrl.current, to.current);
      const m = new THREE.Matrix4().lookAt(tmpAhead, tmp, THREE.Object3D.DEFAULT_UP);
      group.current.quaternion.slerp(
        new THREE.Quaternion().setFromRotationMatrix(m),
        0.12
      );
    } else {
      group.current.quaternion.slerp(restQuat, 0.08);
    }

    // Wings match the perched letter's colour, fading back to default in flight.
    wingMats.current.forEach((m) => {
      const c = flying.current ? m.userData.origColor : press.current.color;
      const e = flying.current ? m.userData.origEmissive : press.current.color;
      m.color.lerp(c, 0.08);
      m.emissive.lerp(e, 0.08);
    });
  });

  return <primitive ref={group} object={scene} scale={BUTTERFLY_SCALE} />;
}

// Scales the whole block so the widest line fills ~90% of the viewport width
// (and never overflows vertically), keeping it readable on any screen.
function FitGroup({ layout, press, inView }) {
  const { viewport } = useThree();
  const scale = Math.min(
    (viewport.width * 0.9) / layout.maxWidth,
    (viewport.height * 0.75) / layout.height
  );

  // Subtle parallax: ease the whole scene toward the mouse position.
  const tiltRef = useRef();
  useFrame((state) => {
    const g = tiltRef.current;
    if (!g) return;
    const targetY = state.pointer.x * TILT;
    const targetX = -state.pointer.y * TILT;
    g.rotation.y += (targetY - g.rotation.y) * 0.05;
    g.rotation.x += (targetX - g.rotation.x) * 0.05;
  });

  return (
    <group ref={tiltRef} scale={scale}>
      <Suspense fallback={null}>
        <Letters glyphs={layout.glyphs} press={press} />
      </Suspense>
      {inView && (
        <Suspense fallback={null}>
          <Butterfly glyphs={layout.glyphs} press={press} />
        </Suspense>
      )}
    </group>
  );
}

export default function CreativeButterfly() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [layout, setLayout] = useState(null);
  const press = useRef({ index: -1, color: new THREE.Color("#ffffff") });

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Register the chosen font so canvas measuring matches what troika renders,
  // then build the glyph layout.
  useEffect(() => {
    let alive = true;
    const face = new FontFace(FONT_FAMILY, `url(${FONT})`);
    face
      .load()
      .then((f) => {
        document.fonts.add(f);
        if (alive) setLayout(computeLayout(FONT_FAMILY));
      })
      .catch(() => alive && setLayout(computeLayout("sans-serif")));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen bg-[var(--home-bg)] overflow-hidden"
    >
      <Canvas camera={{ position: [0, 0, 8], fov: 42 }} gl={{ alpha: true }}>
        <ambientLight intensity={1.1} />
        <directionalLight position={[4, 6, 5]} intensity={2} />
        <directionalLight position={[-5, 2, 3]} intensity={0.6} color="#c4b5fd" />
        {layout && <FitGroup layout={layout} press={press} inView={inView} />}
        {/* <OrbitControls /> */}
      </Canvas>
    </section>
  );
}

useGLTF.preload("/animated_butterfly.glb");
